import { DEPARTMENT_ORDER, DEPARTMENTS } from '@/dictionary/departments';
import { BRAND, NAV } from '@/dictionary/common';
import { departmentPath, HOME_ANCHORS, ROUTES } from '@/dictionary/routes';
import { useScrolledPast } from '@/hooks/useScrolledPast';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LOGO_URL } from '@/config/assets';
import classes from './SiteHeader.module.css';
import type { SiteHeaderProps } from './SiteHeader.types';

const SCROLL_THRESHOLD = 40;

export function SiteHeader({ alwaysVisible = false }: SiteHeaderProps) {
  const hasScrolled = useScrolledPast(SCROLL_THRESHOLD);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isVisible = alwaysVisible || hasScrolled;
  const headerClasses = [classes.header, isVisible ? classes.visible : ''].filter(Boolean).join(' ');
  const linksClasses = [classes.links, isMenuOpen ? classes.linksOpen : ''].filter(Boolean).join(' ');
  const closeMenu = (): void => setIsMenuOpen(false);

  return (
    <header className={headerClasses}>
      <div className={classes.inner}>
        <Link to={ROUTES.home} className={classes.logo} onClick={closeMenu}>
          <img src={LOGO_URL} alt={BRAND.logoAlt} />
        </Link>

        <button
          type="button"
          className={classes.burger}
          aria-label={NAV.menuLabel}
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
        </button>

        <nav className={linksClasses}>
          <div className={classes.dropdown}>
            <Link
              to={`${ROUTES.home}${HOME_ANCHORS.departments}`}
              className={classes.link}
              onClick={closeMenu}
            >
              {NAV.departments}
            </Link>
            <div className={classes.dropdownMenu}>
              {DEPARTMENT_ORDER.map((slug) => {
                const department = DEPARTMENTS[slug];

                if (department.externalUrl) {
                  return (
                    <a
                      key={slug}
                      href={department.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.dropdownLink}
                      onClick={closeMenu}
                    >
                      {department.title}
                    </a>
                  );
                }

                return (
                  <Link
                    key={slug}
                    to={departmentPath(slug)}
                    className={classes.dropdownLink}
                    onClick={closeMenu}
                  >
                    {department.title}
                  </Link>
                );
              })}
            </div>
          </div>

          <Link to={ROUTES.advisors} className={classes.link} onClick={closeMenu}>
            {NAV.advisors}
          </Link>
          <Link to={ROUTES.tracks} className={classes.link} onClick={closeMenu}>
            {NAV.tracks}
          </Link>
          <Link to={ROUTES.about} className={classes.link} onClick={closeMenu}>
            {NAV.about}
          </Link>
          <Link
            to={`${ROUTES.home}${HOME_ANCHORS.contact}`}
            className={`${classes.link} ${classes.cta}`}
            onClick={closeMenu}
          >
            {NAV.contactCta}
          </Link>
        </nav>
      </div>
    </header>
  );
}
